import type {
  Capabilities,
  DocType,
  PermissionName,
} from "../../../clients/graphqlTypes.js";
import type { DocumentByType } from "../../auth/RestrictedByDocumentPermissionByType.js";
import { useUserData } from "../../auth/useUserData.js";

/**
 * The expression-write permission of each doc type, as the `@permissions`
 * directives on the expression mutations name it.
 */
export const EXPRESSIONS_UPDATE_PERMISSION_BY_DOC_TYPE: Partial<
  Record<DocType, PermissionName>
> = {
  QUOTE: "quote_expressions_update",
  REQUIREMENT: "requirement_expressions_update",
  TEMPLATE: "template_expressions_update",
  BUILDING: "building_expressions_update",
};

/**
 * Whether the viewer may write expressions on this document — the boolean form
 * of the `RestrictedByCapabilityAndDocumentPermissionByType` gate, for the
 * handlers that decide what to send rather than what to render. A doc type the
 * record does not name carries no expression permission, which the render gate
 * treats as permitted too.
 */
export function useCanWriteExpressions(
  document: DocumentByType,
  alsoRequires: ReadonlyArray<Capabilities> = []
): boolean {
  const { hasCapability } = useUserData();
  const permission =
    EXPRESSIONS_UPDATE_PERMISSION_BY_DOC_TYPE[document.docType];

  return (
    ["expressions" as const, ...alsoRequires].every(capability =>
      hasCapability(capability)
    ) &&
    (!permission || document.viewerPermissions.includes(permission))
  );
}
